import { useEffect, useRef, useState } from "react";

import { ASC, DESC, RowProps, TableProps } from "..";

import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { commonSiteCoreKeys } from "../../helpers/constants/siteCoreKeys";
import { useMediaQueries } from "../../hooks/useMediaQueries";
import { useNavigationSiteCore } from "../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../hooks/useSiteCore";
import { getSchema, sortTableData } from "./helper";

export const useMuiTableHook = (props: TableProps) => {
  const { commonLabels, fetchValidationMessage } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const validPageNumberMessageText = fetchValidationMessage(
    commonSiteCoreKeys.enterValidPageNumber,
  );

  const {
    tableResults,
    priorityResults = [],
    tableHeaderForm,
    columns,
    pageId,
    noDataInApi,
    emptyMessage = `${commonLabels.noResult}`,
    noResultMessage = `${commonLabels.searchCriteria}`,
    onRowSelected,
    footerColSpan = 2,
    emptyMessageColSpan = 6,
    isGroupView,
    isFooterVisible = true,
    isLastColumnSticky = false,
    selectedId,
    selectedIdKey = "",
    rowIdKey = "id",
    isShowDashIfValueEmpty = false,
  } = props;

  const [page, setPage] = useState(0);
  const [rowsPerPageTable, setRowsPerPageTable] = useState(10);
  // State for sorting
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | "">("");
  const [filteredResults, setFilteredResults] = useState([
    ...priorityResults,
    ...tableResults,
  ]);

  const [sortClickCount, setSortClickCount] = useState<number>(0);

  const [showToolTip, setShowToolTip] = useState(true);

  const tableContainerRef = useRef<HTMLDivElement>(null);

  const handleToolTipCloseAction = () => {
    setShowToolTip(false);
  };

  const { isMobile } = useMediaQueries();

  const {
    getValues,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    mode: "all",
    defaultValues: {
      tablePaginationPageNo: 1,
    },
    resolver: yupResolver(
      getSchema(
        validPageNumberMessageText,
        fetchValidationMessage,
        commonSiteCoreKeys,
        priorityResults,
        tableResults,
        rowsPerPageTable,
      ),
    ),
  });

  useEffect(() => {
    let updatedRows = [...priorityResults, ...tableResults];

    // only in-case of checkbox selection, If a sort is currently applied, re-sort the incoming data
    if (sortColumn && sortDirection) {
      const sortedTableResults = sortTableData(
        tableResults,
        sortColumn,
        sortDirection,
        "",
        true,
      );

      const sortedPriorityResults = priorityResults.length
        ? sortTableData(priorityResults, sortColumn, sortDirection, "", true)
        : [];

      updatedRows = [...sortedPriorityResults, ...sortedTableResults];
    }

    setFilteredResults(updatedRows);
  }, [tableResults, priorityResults]);

  // Row identity (not row values) decides if results changed, so flag toggles like checkboxes keep scroll.
  // Rows without an identity yield null and are always treated as a result change.
  const getRowKeys = (rows: typeof tableResults) =>
    rows.map((row) => {
      const key = row[rowIdKey];
      return key === undefined || key === null ? null : String(key);
    });

  const previousRowKeysRef = useRef(getRowKeys(tableResults));
  useEffect(() => {
    const prevKeys = previousRowKeysRef.current;
    const nextKeys = getRowKeys(tableResults);
    previousRowKeysRef.current = nextKeys;

    const isSameRows =
      prevKeys.length === nextKeys.length &&
      prevKeys.every((key, i) => key !== null && key === nextKeys[i]);

    if (!isSameRows && tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  }, [tableResults]);

  useEffect(() => {
    setTimeout(() => {
      setShowToolTip(!!errors.tablePaginationPageNo);
    }, 0);
  }, [watch("tablePaginationPageNo")]);

  const resetPagination = (shouldResetRowsPerPage: boolean = false) => {
    setPage(0);
    reset();
    setSortColumn(null);

    if (shouldResetRowsPerPage) {
      setRowsPerPageTable(10);
    }
  };

  const goToPageAction = () => {
    const { tablePaginationPageNo } = getValues();
    const totalPages = Math.ceil(
      [...priorityResults, ...tableResults].length / rowsPerPageTable,
    );

    if (
      !tablePaginationPageNo ||
      tablePaginationPageNo === null ||
      tablePaginationPageNo <= 0 ||
      tablePaginationPageNo > totalPages
    ) {
      setShowToolTip(true);
      return;
    }
    // starts from 0
    setPage(+tablePaginationPageNo - 1);
  };

  const handleChangePageAction = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPageAction = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPageTable(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowSelectedAction = (row: RowProps) => {
    onRowSelected?.(row);
  };

  const handlePageChangeAction = (
    event: React.ChangeEvent<unknown>,
    newPage: number,
  ) => {
    setPage(newPage - 1);
  };

  const handleSort = (
    column: string,
    objectKeyName?: string,
    isEmptyValue?: boolean,
    sortKeyName?: string,
    considerSortAsString: boolean = false,
  ) => {
    if (sortColumn === column) {
      const currentClick = sortClickCount + 1;
      if (currentClick === 3) {
        // when clicking on 3rd time then reset sorting
        setSortClickCount(0);
        setSortColumn(null);
        setSortDirection("");
        setFilteredResults([...priorityResults, ...tableResults]);
        return;
      } else {
        setSortClickCount(currentClick);
      }
    } else {
      setSortClickCount(1);
    }

    const isAsc = sortColumn === column && sortDirection === ASC;

    const direction = isAsc ? DESC : ASC;
    setSortColumn(column);
    setSortDirection(direction);
    const sortedRows = sortTableData(
      tableResults,
      column,
      direction,
      objectKeyName,
      isEmptyValue,
      sortKeyName,
      considerSortAsString,
    );
    if (priorityResults.length === 0) {
      setFilteredResults([...sortedRows]);
    } else {
      const sortedPriorityResults = sortTableData(
        priorityResults,
        column,
        direction,
        objectKeyName,
        isEmptyValue,
        undefined,
        considerSortAsString,
      );

      setFilteredResults([...sortedPriorityResults, ...sortedRows]);
    }
  };

  const getColspan = () => (isMobile ? columns.length : 4);

  const getSiblingCount = () => (isMobile ? 0 : 1);

  const getEmptyMessageAlignment = () => (isMobile ? "left" : "center");
  const getEmptyMessage = () => (noDataInApi ? emptyMessage : noResultMessage);

  return {
    page,
    setPage,
    rowsPerPage: rowsPerPageTable,
    setRowsPerPage: setRowsPerPageTable,
    sortColumn,
    sortDirection,
    filteredResults,
    handleSort,
    handleChangePage: handleChangePageAction,
    handleChangeRowsPerPage: handleChangeRowsPerPageAction,
    goToPage: goToPageAction,
    showToolTip,
    setShowToolTip,
    control,
    reset,
    watch,
    errors,
    commonLabels,
    commonARIALabel,
    getValues,
    priorityResults,
    tableResults,
    handleRowSelected: handleRowSelectedAction,
    resetPagination,
    handlePageChange: handlePageChangeAction,
    footerColSpan,
    emptyMessageColSpan,
    isGroupView,
    isFooterVisible,
    isLastColumnSticky,
    selectedId,
    selectedIdKey,
    isShowDashIfValueEmpty,
    columns,
    pageId,
    tableHeaderForm,
    onRowSelected,
    sortClickCount,
    isMobile,
    handleToolTipClose: handleToolTipCloseAction,
    getColspan,
    getSiblingCount,
    getEmptyMessageAlignment,
    getEmptyMessage,
    tableContainerRef,
  };
};
